import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { Merchant } from '../network/API/types';
import { query } from '../network/merchant';

type RecordState = {
  scope: string;
  status: 'loading' | 'ready' | 'error';
  merchant: Merchant | null;
};

/** Load only the current session's merchant; failed or stale lookups never mean unlimited. */
export const useMerchantRecord = (merchantUID: string, sessionId?: string) => {
  const scope = `${sessionId ?? ''}:${merchantUID}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const requestId = useRef(0);
  const [record, setRecord] = useState<RecordState>({
    scope,
    status: 'loading',
    merchant: null,
  });

  const reload = useCallback(async () => {
    const request = ++requestId.current;
    setRecord({ scope, status: 'loading', merchant: null });
    if (!sessionId || !merchantUID) return undefined;
    try {
      const response = await query(merchantUID);
      if (currentScope.current !== scope || request !== requestId.current)
        return undefined;
      if (response.errors || response.data?.merchant_uid !== merchantUID) {
        setRecord({ scope, status: 'error', merchant: null });
        return undefined;
      }
      setRecord({ scope, status: 'ready', merchant: response.data });
      return response.data;
    } catch {
      if (currentScope.current === scope && request === requestId.current) {
        setRecord({ scope, status: 'error', merchant: null });
      }
    }
    return undefined;
  }, [merchantUID, scope, sessionId]);

  useEffect(() => {
    const requests = requestId;
    reload();
    // Invalidate in-flight loads on scope changes and unmount.
    return () => {
      ++requests.current;
    };
  }, [reload]);

  const setMerchant: Dispatch<SetStateAction<Merchant | null>> = useCallback(
    update => {
      setRecord(previous => {
        // Settings may update a loaded record, but cannot restore another merchant's state.
        if (previous.scope !== scope || currentScope.current !== scope)
          return previous;
        const merchant =
          typeof update === 'function' ? update(previous.merchant) : update;
        return {
          scope,
          status: merchant?.merchant_uid === merchantUID ? 'ready' : 'loading',
          merchant: merchant?.merchant_uid === merchantUID ? merchant : null,
        };
      });
    },
    [scope, merchantUID],
  );

  const current = record.scope === scope && !!sessionId && !!merchantUID;
  return {
    merchant: current && record.status === 'ready' ? record.merchant : null,
    status: current ? record.status : 'loading',
    setMerchant,
    reload,
  };
};
